import config from 'config'
import { Channel, Socket } from 'phoenix'
import { log } from 'utils'
import { resolvePresenceAPIBase, resolveWSAPIBase } from 'utils/api'
import { SOCKET_CLUSTER } from 'utils/socket'

interface OpenSocketArgs {
  session_token: string,
  org_id: number,
  socketCluster: string,
  ip: string
}

interface JoinChannelArgs {
  channelName: string,
  socket: Socket,
  events: any
}

export const OpenSocket = ({ session_token, org_id, socketCluster, ip }: OpenSocketArgs) => {
  const protocol = config.WS_PROTOCOL
  const orgPrefix = config.WS_USER_ENV === 'LOCAL'
    ? ``
    : `${org_id}-`

  const socketBase = _resolveSocketBaseForType(org_id, orgPrefix, socketCluster)

  const socketUrl = `${protocol}://${socketBase}/ws`
  const socket = new Socket(socketUrl, {
    params: { session_token, remote_ip: ip },
    timeout: 10000,
    reconnectAfterMs: (attempt) => [1000, 2000, 5000, 10000][attempt - 1] || 10000,
    rejoinAfterMs: (tries) => [1000, 2000, 5000][tries - 1] || 10000
  })

  socket.connect()

  socket.onError((...args) => {
    log({ data: [`Failed to connect to socket: ${socketUrl}`, ...args]  })
  })

  socket.onClose(() => log({ data: [`socket ${socketUrl} has closed`]}))

  return socket
}

export const JoinChannel = async ({ channelName, socket, events}: JoinChannelArgs) => {
  const ChannelPromise = new Promise<{ channel: Channel }>(async (resolve, reject) => {
    const channel = socket.channel(channelName)

    events.forEach(({ topic, event }) => {
      channel.on(topic, event)
    })

    channel.join()
      .receive("ok", () => {
        resolve({ channel })
      })
      .receive("error", err => {
        log({ data: [`Error connecting to channel: ${channelName}`] })
        reject(err)
      })
      .receive("timeout", err => {
        log({ data: [`Joining the ${channelName} channel timed out. `] })
      })
  })

  return ChannelPromise
}

const _resolveSocketBaseForType = (orgId: number, orgPrefix: string, socketCluster: string) => {
  switch(socketCluster) {
    case SOCKET_CLUSTER.CHAT: return `${resolveWSAPIBase(orgId)}`
    case SOCKET_CLUSTER.USER: return config.WS_USER_BASE
    case SOCKET_CLUSTER.PRESENCE: return `${orgPrefix}${resolvePresenceAPIBase(orgId)}`
    case SOCKET_CLUSTER.VISITOR_PRESENCE: return config.WS_VISITOR_PRESENCE_BASE
    default: return ''
  }
}
